import React, { SyntheticEvent, useContext } from 'react';
import styled from 'styled-components';

import { FatalAttraction as FA } from '@vpr/tracking';

import { grayscale, slate } from '@vimeo/iris/color';
import { Button } from '@vimeo/iris/components';
import { DownloadArrow } from '@vimeo/iris/icons';
import { themes } from '@vimeo/iris/themes';
import { Link, Paragraph } from '@vimeo/iris/typography';
import { TeamUserPermissionLevel, User } from '@vimeo/viewer';
import { ViewerContext } from '@vimeo/viewer';

import AlbumConfigContext from '../../containers/AlbumConfigContext';
import { EmbedShowcaseContext } from '../../context/useEmbedShowcase';
import { useShowcaseEmbed } from '../../hooks/useShowcaseEmbed';
import { AlbumThemes, ClipUser, Pictures } from '../../types';
import { STOCK_PRIVACY, THEME_DARK } from '../../utils/constants';
import stockService from '../../utils/stock';
import EssentialsBadge from '../EssentialsBadge';
import UserLink from '../UserLink';
import ClipStockDetails from './ClipStockDetails';
import { ClipCardWrapper, ClipTitle, DescriptionBox } from './Styles';

interface Props {
  albumId: number;
  albumHash?: string;
  theme: AlbumThemes;
  user?: ClipUser;
  clipId: number;
  isFree?: boolean;
  curUser?: User;
  isOffsite: boolean;
  allowDownloads: boolean;
  setDownloadClipId?: (id: number) => void;
  openDownloadModal?: () => void;
  trackClipClick?: () => void;
  link: string;
  name: string;
  description?: string;
  privacy: {
    view: string;
    download: boolean;
  };
  pictures: Pictures;
}

const PlayerWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: block;
  overflow: hidden;
  position: relative;

  &::after {
    content: ' ';
    padding-bottom: 56.25%;
    display: block;
  }

  iframe {
    border: none;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const BadgeWrapper = styled.div`
  position: absolute;
  display: inline-block;
  height: 19px;
  background-color: ${grayscale(850)};
  top: 10px;
  left: 10px;
  border-radius: 2px;
  z-index: 10;
`;

const DownloadWrapper = styled.div`
  position: absolute;
  right: 10px;
  bottom: 12px;
`;

const StyledDownloadIcon = styled(DownloadArrow)<{ isDarkTheme: boolean }>`
  path {
    fill: ${(props) => (props.isDarkTheme ? 'white' : undefined)};
  }
`;

const StyledLink = styled(Link)<{ isDarkTheme: boolean }>`
  color: ${(props) => (props.isDarkTheme ? 'white' : slate(800))};
  &:hover,
  &:link,
  &:active,
  &:visited {
    color: ${(props) => (props.isDarkTheme ? 'white' : slate(800))} !important;
  }
`;

function ClipCard(props: Props) {
  const viewer = useContext(ViewerContext);
  const { hashedPass } = useContext(AlbumConfigContext);
  const embedShowcaseData = useContext(EmbedShowcaseContext);
  const { getEmbedQueryParam } = useShowcaseEmbed();

  const vimeoHttpsUrl = viewer && viewer.vimeoHttpsUrl;
  const isImpersonatingViewerOrContrib =
    viewer?.user?.teamUserPermissionLevel &&
    (viewer.user.teamUserPermissionLevel === TeamUserPermissionLevel.Contributor ||
      viewer.user.teamUserPermissionLevel === TeamUserPermissionLevel.Viewer);

  const {
    link,
    user,
    name,
    description,
    theme,
    privacy,
    clipId,
    pictures,
    isFree,
    curUser,
    albumId,
    albumHash,
    isOffsite,
    allowDownloads,
    setDownloadClipId,
    openDownloadModal,
    trackClipClick,
  } = props;

  const isDarkTheme = theme === THEME_DARK;
  const isStock = privacy.view === STOCK_PRIVACY;
  const stockClipPageLink = `${vimeoHttpsUrl}/${clipId}`;
  const clipPageLink = `${vimeoHttpsUrl}${link}${getEmbedQueryParam('?')}`;
  const showDownloadButton = !isStock && allowDownloads && privacy.download;

  if (user === null) {
    FA.trackEvent({
      container: 'showcase_error_clip_has_null_user',
      component: `albumId: ${albumId}`,
      keyword: `clipId: ${clipId}`,
    });
  }

  const unlistedHashQs = albumHash ?? null;
  let iframeUrl = `${vimeoHttpsUrl}/showcase/${albumId}/video/${clipId}/embed`;

  if (hashedPass) {
    iframeUrl += `?hash=${hashedPass}`;
  }

  if (unlistedHashQs) {
    const prefix = hashedPass ? '&' : '?';
    iframeUrl += `${prefix}sh=${unlistedHashQs}`;
  }

  if (embedShowcaseData?.isShowcaseEmbeded) {
    const prefix = hashedPass || unlistedHashQs ? '&' : '?';
    iframeUrl += getEmbedQueryParam(prefix);
  }

  return (
    <ClipCardWrapper onClick={trackClipClick}>
      {isFree && (
        <BadgeWrapper>
          <EssentialsBadge />
        </BadgeWrapper>
      )}
      <PlayerWrapper>
        <iframe allowFullScreen scrolling="no" src={iframeUrl} title={name} />
      </PlayerWrapper>
      <DescriptionBox darkTheme={isDarkTheme}>
        <ClipTitle>
          {isOffsite || isImpersonatingViewerOrContrib ? (
            name
          ) : (
            <StyledLink
              format="basic"
              variant="minimal"
              isDarkTheme={isDarkTheme}
              href={isStock ? stockClipPageLink : clipPageLink}
            >
              {name}
            </StyledLink>
          )}
        </ClipTitle>
        {user && (
          <UserLink
            theme={theme}
            isImpersonatingViewerOrContrib={!!isImpersonatingViewerOrContrib}
            isOffsite={isOffsite}
            user={user}
          />
        )}
        {isStock ? (
          <ClipStockDetails
            clipId={clipId}
            pictures={pictures}
            name={name}
            theme={theme}
            stockService={stockService}
            user={curUser}
            isFree={isFree}
          />
        ) : description ? (
          <Paragraph size="2" style={{ marginRight: `${showDownloadButton ? '30px' : '20px'}` }}>
            {description}
          </Paragraph>
        ) : null}
        {showDownloadButton && (
          <DownloadWrapper>
            <Button
              icon={<StyledDownloadIcon isDarkTheme={isDarkTheme} />}
              format="soft"
              variant="minimalTransparent"
              size="md"
              onClick={(e: SyntheticEvent) => {
                e.stopPropagation();
                // handle download
                if (setDownloadClipId && openDownloadModal) {
                  setDownloadClipId(clipId);
                  openDownloadModal();
                }
              }}
            />
          </DownloadWrapper>
        )}
      </DescriptionBox>
    </ClipCardWrapper>
  );
}

export default ClipCard;
